import { createContext, useCallback, useContext, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Check } from "lucide-react";

const COLORS = ["#f29e1b", "#14b8a6", "#8b5cf6", "#f43f5e", "#0ea5e9", "#84cc16"];
const PRAISE = ["Nice!", "Yes!", "Great!", "Boom!", "Woohoo!"];
const SPARKS = 14;
const LIFETIME = 1000;

interface Burst {
  id: number;
  x: number;
  y: number;
  praise: string;
}

const TaskBurstContext = createContext<(anchor: Element) => void>(() => {});

/**
 * The little party when a kid finishes a task. It lives up here because the tapped row can
 * disappear as soon as the task counts as done, and the burst should outlast it.
 */
export function TaskBurstProvider({ children }: { children: ReactNode }) {
  const [bursts, setBursts] = useState<Burst[]>([]);
  const nextId = useRef(1);

  const burst = useCallback((anchor: Element) => {
    const rect = anchor.getBoundingClientRect();
    const id = nextId.current++;
    const praise = PRAISE[id % PRAISE.length]!;
    setBursts((list) => [...list, { id, x: rect.left + rect.width / 2, y: rect.top + rect.height / 2, praise }]);
    setTimeout(() => setBursts((list) => list.filter((b) => b.id !== id)), LIFETIME);
  }, []);

  return (
    <TaskBurstContext.Provider value={burst}>
      {children}
      <div className="pointer-events-none fixed inset-0 z-[90] overflow-hidden" aria-hidden>
        {bursts.map((b) => (
          <div key={b.id} className="absolute" style={{ left: b.x, top: b.y }}>
            <span className="task-burst-ring absolute -left-8 -top-8 size-16 rounded-full border-4 border-brand" />
            {Array.from({ length: SPARKS }).map((_, i) => {
              const angle = (i / SPARKS) * 2 * Math.PI;
              const reach = 54 + ((i * 17) % 34);
              return (
                <span
                  key={i}
                  className="task-burst-spark absolute -left-1.5 -top-1.5 block size-3 rounded-sm"
                  style={
                    {
                      background: COLORS[i % COLORS.length],
                      "--x": `${Math.round(Math.cos(angle) * reach)}px`,
                      "--y": `${Math.round(Math.sin(angle) * reach)}px`,
                    } as CSSProperties
                  }
                />
              );
            })}
            <span data-testid="task-burst" className="task-burst-check absolute -left-7 -top-7 flex size-14 items-center justify-center rounded-full bg-brand text-on-brand shadow-card">
              <Check className="size-9" strokeWidth={3.5} />
            </span>
            <span className="task-burst-label absolute -top-16 left-0 whitespace-nowrap text-2xl font-black text-brand-strong">{b.praise}</span>
          </div>
        ))}
      </div>
    </TaskBurstContext.Provider>
  );
}

export const useTaskBurst = () => useContext(TaskBurstContext);
